/**
 * Service de synchronisation intelligente pour la PWA
 * Gère la synchronisation incrémentale des données avec l'API
 */

// Intervalle de synchronisation périodique (2 minutes)
const PERIODIC_SYNC_INTERVAL_MS = 2 * 60 * 1000

/**
 * Seuil de pages au-delà duquel on préfère télécharger le fichier export gzip
 * plutôt que de paginer via /modified.
 * Ex : si 11 pages × 100 enregistrements = 1 100 modifications → export plus rapide.
 */
const EXPORT_PAGE_THRESHOLD = 10

class SyncService {
  constructor() {
    this.apiService = null
    this.dexieService = null
    this.isSyncing = false
    this.syncInProgress = {}

    // ── État de progression de la sync incrémentale ──────────────────────
    this._syncProgress = {
      isSyncing: false,
      currentEntity: null,
      currentPage: 0,
      totalPages: 0,
      totalFetched: 0,
      // Progression export ZIP — phase "saving" (enregistrements)
      loaded: 0,
      total: 0,
      // Progression export ZIP — phase "downloading" (octets)
      phase: null,           // 'downloading' | 'saving' | null
      downloadedBytes: 0,
      totalDownloadBytes: 0
    }
    this._listeners = []

    // ── Scheduler de sync périodique ─────────────────────────────────────
    this._syncTimer = null

    // ── Cooldown anti-rebond ──────────────────────────────────────────────
    // Timestamp (ms) de la dernière smartSync complétée, pour éviter des
    // déclenchements en rafale (ex: retour en ligne + timer périodique simultanés).
    this._lastSmartSyncAt = null
  }

  // ── API Observateur ────────────────────────────────────────────────────

  /**
   * S'abonner aux changements d'état de sync incrémentale.
   * @param {Function} listener  Appelé avec (syncProgress) à chaque changement
   * @returns {Function} Fonction de désabonnement
   */
  /**
   * Réinitialise la date de sync d'une entité pour forcer une resynchronisation
   * depuis une date spécifique.
   * Utile quand des enregistrements ont été ajoutés en DB après la génération de l'export
   * mais avant la dernière sync, créant un "trou" invisible pour la sync incrémentale.
   *
   * @param {string} entity - Nom de l'entité (ex: 'properties')
   * @param {string} dateString - Date ISO ou 'YYYY-MM-DD HH:mm:ss' depuis laquelle resynchroniser
   */
  async resetEntitySyncDate(entity, dateString) {
    try {
      const { sync } = await import('./dexieService')
      await sync().put({
        entity,
        lastSyncDate: dateString,
        exportGenerationDate: null
      })
    } catch (error) {
      console.error(`[SyncService] Erreur lors de la réinitialisation de la sync pour "${entity}":`, error)
      throw error
    }
  }

  addSyncProgressListener(listener) {
    this._listeners.push(listener)
    // Émettre immédiatement l'état courant au nouveau listener
    listener({ ...this._syncProgress })
    return () => this.removeSyncProgressListener(listener)
  }

  removeSyncProgressListener(listener) {
    this._listeners = this._listeners.filter(l => l !== listener)
  }

  /** @private */
  _emitProgress(patch) {
    Object.assign(this._syncProgress, patch)
    const snapshot = { ...this._syncProgress }
    this._listeners.forEach(l => {
      try { l(snapshot) } catch (e) { /* ne pas bloquer la sync */ }
    })
  }

  async init() {
    if (!this.apiService) {
      this.apiService = (await import('./api')).default
    }
    if (!this.dexieService) {
      this.dexieService = (await import('./dexieService')).default
    }
  }

  /**
   * Synchronisation intelligente principale
   */
  async smartSync() {
    await this.init()

    if (this.isSyncing) {
      return { skipped: true, reason: 'sync_in_progress' }
    }

    // Cooldown : éviter deux syncs en moins de 60 secondes
    // (protection contre les déclenchements en rafale : retour en ligne + timer périodique simultanés)
    const COOLDOWN_MS = 60000
    if (this._lastSmartSyncAt && (Date.now() - this._lastSmartSyncAt) < COOLDOWN_MS) {
      return { skipped: true, reason: 'cooldown' }
    }

    this.isSyncing = true

    try {
      // 1. Vérifier si première connexion (tables vides)
      const isFirstSync = await this.isFirstSync()
      
      if (isFirstSync) {
        const result = await this.fullSync()
        this.isSyncing = false
        return result
      }

      // 2. Récupérer les dates de dernière sync locales
      const entities = ['properties', 'controls', 'teams', 'users', 
                       'dictionaries', 'assistanceMandators', 'managerMandators', 
                       'legalOwners', 'interventionPoints']
      
      const localSyncDates = {}
      for (const entity of entities) {
        const localSync = await this.getLocalSyncInfo(entity)
        if (localSync && localSync.lastSyncDate) {
          localSyncDates[entity] = localSync.lastSyncDate
        }
      }

      // 3. Obtenir les dates de dernière modification depuis /health/sync
      const health = await this.apiService.publicRequest('/health/sync')
      
      if (!health || !health.lastModified) {
        this.isSyncing = false
        return { success: false, reason: 'invalid_health_response' }
      }

      // exportGeneration peut être absent si l'API est une ancienne version
      const exportGeneration = health.exportGeneration || null

      // 4. Synchroniser chaque entité
      const syncResults = {}
      
      for (const entity of entities) {
        const apiLastModified = health.lastModified[entity]
        const result = await this.syncEntity(entity, apiLastModified, exportGeneration)
        syncResults[entity] = result
      }

      this.isSyncing = false
      this._lastSmartSyncAt = Date.now()
      return { success: true, results: syncResults }
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation:', error)
      this.isSyncing = false
      this._lastSmartSyncAt = Date.now()
      return { success: false, error: error.message }
    }
  }

  /**
   * Vérifie si c'est la première synchronisation
   */
  async isFirstSync() {
    const propertiesCount = await this.dexieService.countProperties()
    const controlsCount = await this.dexieService.countControls()
    
    return propertiesCount === 0 || controlsCount === 0
  }

  /**
   * Synchronisation complète (première fois)
   */
  async fullSync() {
    // Retourner un indicateur pour utiliser le processus de chargement initial existant
    return { fullSync: true, message: 'Use existing initial loading process' }
  }

  /**
   * Synchronise une entité spécifique.
   *
   * Stratégie selon l'entité :
   *  - assistanceMandators / managerMandators → rafraîchissement complet (pas d'endpoint /modified)
   *  - properties / controls                 → export gzip si régénéré, sinon incrémental
   *  - autres entités                        → incrémental paginé via /modified
   *
   * @param {string}      entity            - Clé de l'entité (ex: 'properties')
   * @param {string|null} apiLastModified   - Date de dernière modification serveur
   * @param {object|null} exportGeneration  - Objet { properties, controls } avec dates de génération des exports
   */
  async syncEntity(entity, apiLastModified, exportGeneration = null) {
    if (this.syncInProgress[entity]) {
      return { synced: false, reason: 'sync_in_progress' }
    }

    this.syncInProgress[entity] = true

    try {
      // 1. Récupérer les infos de sync locales
      let localSync = await this.getLocalSyncInfo(entity)

      // 1b. Auto-guérison : détecter et effacer une lastSyncDate corrompue en IndexedDB.
      // Si la date stockée est invalide ou antérieure à l'an 2000 (ex: "0000-00-00 00:00:00"
      // sauvegardée par un bug de l'API), on supprime l'entrée pour repartir d'une base saine :
      // le prochain cycle détectera l'absence de lastSyncDate et déclenchera un re-téléchargement
      // du ZIP (comportement correct pour une première synchronisation).
      if (localSync?.lastSyncDate) {
        const sanityDate = this._normalizeDate(localSync.lastSyncDate)
        if (!sanityDate || sanityDate.getFullYear() < 2000) {
          console.warn(`[SyncService] ${entity}: lastSyncDate corrompue ("${localSync.lastSyncDate}") → réinitialisation de l'entrée sync`)
          try {
            const { sync } = await import('./dexieService')
            await sync().delete(entity)
          } catch (e) { /* ignore — la sync continuera sans entrée */ }
          localSync = null
        }
      }

      // 2. Déterminer si sync nécessaire.
      // Exception : si un checkpoint actif existe (sync incrémentale interrompue),
      // on force la reprise même si needsSync() renverrait false (ex: aucune nouvelle
      // modification serveur depuis le since du checkpoint, mais des pages restent).
      const hasActiveCheckpoint = localSync?.checkpointSince === localSync?.lastSyncDate
        && localSync?.checkpointPage > 1

      // Exception : un export ZIP plus récent est disponible sur le serveur pour
      // les entités properties/controls. On doit entrer dans syncWithExport() même
      // si needsSync() retourne false (ex: lastSyncDate == apiLastModified mais
      // l'export a été régénéré depuis → de nouvelles données existent dans le ZIP).
      const exportEntities = ['properties', 'controls']
      const hasNewExport = exportEntities.includes(entity)
        && exportGeneration
        && exportGeneration[entity]
        && exportGeneration[entity] !== localSync?.exportGenerationDate

      if (!hasActiveCheckpoint && !hasNewExport && !this.needsSync(localSync, apiLastModified)) {
        this.syncInProgress[entity] = false
        return { synced: false, reason: 'up_to_date' }
      }

      // 3. Synchroniser selon le type d'entité
      const fullRefreshEntities = ['assistanceMandators', 'managerMandators', 'users', 'teams']
      // properties et controls bénéficient du fichier export gzip pré-généré :
      // si le fichier a été régénéré depuis la dernière sync, on le télécharge en entier
      // (beaucoup plus rapide qu'une pagination incrémentale sur gros volume).
      // Si le fichier n'a pas changé, syncWithExport() retombe automatiquement sur syncIncremental().
      // (exportEntities déjà déclaré plus haut dans la même fonction)

      let result
      if (fullRefreshEntities.includes(entity)) {
        result = await this.syncWithFullRefresh(entity, apiLastModified)
      } else if (exportEntities.includes(entity)) {
        result = await this.syncWithExport(entity, localSync, apiLastModified, exportGeneration)
      } else {
        result = await this.syncIncremental(entity, localSync, apiLastModified)
      }

      this.syncInProgress[entity] = false
      return result
    } catch (error) {
      console.error(`❌ Erreur sync ${entity}:`, error)
      this.syncInProgress[entity] = false
      return { synced: false, reason: 'error', error: error.message }
    }
  }

  /**
   * Rafraîchissement complet d'une entité (fetch total + remplacement IndexedDB)
   * Utilisé pour les entités de taille réduite sans endpoint /modified
   */
  async syncWithFullRefresh(entity, apiLastModified) {
    const endpointMap = {
      assistanceMandators: '/assistance-mandators',
      managerMandators: '/manager-mandators',
      users: '/users',
      teams: '/teams',
    }

    const endpoint = endpointMap[entity]
    if (!endpoint) {
      return { synced: false, reason: 'no_endpoint_for_entity' }
    }

    try {
      const data = await this.apiService.request(endpoint)

      if (Array.isArray(data) && data.length > 0) {
        await this.applyIncrementalUpdates(entity, data)
        await this.updateLocalSyncInfo(entity, apiLastModified)
        return { synced: true, method: 'full_refresh', count: data.length }
      }

      return { synced: false, reason: 'empty_data' }
    } catch (error) {
      console.error(`❌ Erreur sync full refresh ${entity}:`, error)
      return { synced: false, reason: 'full_refresh_failed', error: error.message }
    }
  }

  /**
   * Normalise une chaîne de date en objet Date de façon fiable dans tous les navigateurs.
   *
   * Problème : `new Date("2026-06-22 14:13:00")` (format SQL sans T ni Z) est non standard :
   * certains navigateurs l'interprètent en UTC, d'autres en heure locale, d'autres encore
   * retournent Invalid Date.
   *
   * Solution : on remplace l'espace par un 'T' pour obtenir un ISO 8601 valide.
   * Si la date ne contient pas d'indicateur de timezone (Z ou +hh:mm), on la traite
   * comme heure locale (comportement cohérent avec ce que retourne l'API Symfony).
   *
   * @param {string} dateString
   * @returns {Date}
   * @private
   */
  _normalizeDate(dateString) {
    if (!dateString) return null

    // Déjà au format ISO avec T : parser directement
    // Ex: "2026-06-22T12:04:18.440Z" ou "2026-06-22T14:13:00+02:00"
    if (dateString.includes('T')) {
      return new Date(dateString)
    }

    // Format SQL "YYYY-MM-DD HH:mm:ss" → remplacer l'espace par T
    // Le résultat "YYYY-MM-DDTHH:mm:ss" est traité comme heure locale par les navigateurs
    const normalized = dateString.replace(' ', 'T')
    const date = new Date(normalized)

    // Vérification de validité
    if (isNaN(date.getTime())) {
      console.warn(`[SyncService] Date non parseable : "${dateString}"`)
      return null
    }

    return date
  }

  /**
   * Détermine si une synchronisation est nécessaire
   */
  needsSync(localSync, apiLastModified) {
    // Si pas de dernière sync locale, sync nécessaire
    if (!localSync || !localSync.lastSyncDate) {
      return true
    }

    // Si l'API ne fournit pas de date, on ne peut pas comparer
    if (!apiLastModified) {
      return false
    }

    try {
      const localDate = this._normalizeDate(localSync.lastSyncDate)
      const apiDate = this._normalizeDate(apiLastModified)

      if (!apiDate || apiDate.getFullYear() < 2000) {
        // Date API invalide ou date nulle (ex: "0000-00-00 00:00:00") →
        // ne pas déclencher de sync sur une donnée non fiable.
        console.warn(`[SyncService] needsSync: date API non fiable ("${apiLastModified}") → sync ignorée`)
        return false
      }

      if (!localDate || localDate.getFullYear() < 2000) {
        // Date locale corrompue → resynchroniser pour guérir l'état
        return true
      }

      return apiDate > localDate
    } catch (error) {
      return true // En cas de doute, synchroniser
    }
  }

  /**
   * Route intelligente : incrémental (/modified) ou export gzip selon le volume.
   *
   * Stratégie :
   *  1. Pas de lastSyncDate (jamais synchronisé) → export direct si disponible,
   *     sinon incrémental.
   *  2. lastSyncDate présent → sonde la page 1 de /modified pour obtenir totalPages.
   *     - totalPages <= EXPORT_PAGE_THRESHOLD  → incrémental (peu de données)
   *     - totalPages >  EXPORT_PAGE_THRESHOLD  → export gzip si disponible
   *     - Pas d'export disponible → incrémental quelle que soit la taille
   *
   * Note : la sonde fait une seule requête page 1 (100 enregistrements max).
   * Côté performance, cette requête est identique à la première page d'une sync
   * incrémentale normale, donc le surcoût est négligeable.
   *
   * @param {string}      entity           - 'properties' | 'controls'
   * @param {object|null} localSync        - Infos de sync stockées dans IndexedDB
   * @param {string|null} apiLastModified  - Date de dernière modification serveur
   * @param {object|null} exportGeneration - { properties, controls } dates de génération
   */
  async syncWithExport(entity, localSync, apiLastModified, exportGeneration) {
    const apiExportDate = exportGeneration ? exportGeneration[entity] : null

    // ── 1. Pas de sync précédente : export direct si disponible ──────────
    if (!localSync?.lastSyncDate) {
      if (apiExportDate) {
        return await this._downloadExport(entity, apiLastModified, apiExportDate)
      }
      // Pas d'export disponible → incrémental (qui échouera sur no_last_sync_date,
      // mais c'est le comportement attendu : rien à sync sans date de départ)
      return await this.syncIncremental(entity, localSync, apiLastModified)
    }

    // ── 1b. L'export a été régénéré sur le serveur → téléchargement direct du ZIP ──
    // Si exportGenerationDate locale ≠ date de l'export serveur, le ZIP a changé.
    // On le télécharge directement sans sonder /modified : la sonde mesurerait le
    // delta depuis lastSyncDate, qui peut être à jour (0 résultats) même si le ZIP
    // contient de nouvelles données, et conclurait à tort "peu de changements → incrémental".
    if (apiExportDate && localSync.exportGenerationDate !== apiExportDate) {
      return await this._downloadExport(entity, apiLastModified, apiExportDate, localSync.lastSyncDate)
    }

    // ── 1c. L'export courant a déjà été téléchargé → incrémental ──────────
    // exportGenerationDate locale === date de l'export serveur : le ZIP n'a pas changé.
    // On passe en incrémental pour récupérer uniquement le delta depuis lastSyncDate.
    if (apiExportDate && localSync.exportGenerationDate === apiExportDate) {
      return await this.syncIncremental(entity, localSync, apiLastModified)
    }

    // ── 2. Pas d'export gzip disponible → incrémental direct ─────────────
    if (!apiExportDate) {
      return await this.syncIncremental(entity, localSync, apiLastModified)
    }

    // ── 3. Sonde page 1 pour connaître le volume à synchroniser ──────────
    // (cas résiduel : export disponible mais exportGenerationDate locale absente)
    const since = localSync.lastSyncDate
    try {
      // La sonde utilise la même PAGE_LIMIT que syncIncremental pour que le
      // nombre de pages calculé soit cohérent avec la charge réelle de la sync.
      const probeEndpoint = `/${entity}/modified?since=${encodeURIComponent(since)}&page=1&limit=1000`
      const probeResponse = await this.apiService.request(probeEndpoint)

      if (!probeResponse || !Array.isArray(probeResponse.data)) {
        return { synced: false, reason: 'invalid_probe_response' }
      }

      const totalPages = probeResponse.totalPages || 1

      // Peu de modifications → incrémental classique
      if (totalPages <= EXPORT_PAGE_THRESHOLD) {
        return await this.syncIncremental(entity, localSync, apiLastModified)
      }

      // Beaucoup de modifications → export gzip plus efficace
      return await this._downloadExport(entity, apiLastModified, apiExportDate, since)

    } catch (error) {
      if (error.response?.status === 404) {
        // Endpoint /modified inexistant pour cette entité
        await this.updateLocalSyncInfo(entity, apiLastModified, localSync?.exportGenerationDate)
        return { synced: true, method: 'skipped', count: 0 }
      }
      // En cas d'erreur de sonde, tenter l'incrémental en fallback
      console.warn(`[SyncService] Sonde échouée pour ${entity}, fallback incrémental :`, error.message)
      return await this.syncIncremental(entity, localSync, apiLastModified)
    }
  }

  /**
   * Télécharge le fichier export gzip et l'enregistre en IndexedDB.
   * Notifie la navbar via _emitProgress() pendant toute la durée du téléchargement.
   *
   * @param {string}      entity          - 'properties' | 'controls'
   * @param {string}      apiLastModified - Dernière date de modification serveur
   * @param {string}      apiExportDate   - Date de génération de l'export (ISO)
   * @param {string|null} lastSyncDate    - Date de la dernière sync locale (optionnel, pour l'affichage)
   * @private
   */
  async _downloadExport(entity, apiLastModified, apiExportDate, lastSyncDate = null) {
    // Récupérer le nombre d'enregistrements actuellement en cache comme estimation
    // du total à télécharger. Cela permet d'afficher une progression significative
    // dès que la sync démarre, même avant de connaître le total réel.
    let estimatedTotal = 0
    try {
      if (entity === 'properties') {
        estimatedTotal = await this.dexieService.countProperties()
      } else if (entity === 'controls') {
        estimatedTotal = await this.dexieService.countControls()
      }
    } catch (e) { /* en silence — l'absence de total est gérée côté UI */ }

    // Émission initiale : phase "downloading", total estimé depuis Dexie
    this._emitProgress({
      isSyncing: true,
      currentEntity: entity,
      method: 'export',
      phase: 'downloading',
      currentPage: 0,
      totalPages: 0,
      totalFetched: 0,
      loaded: 0,
      total: estimatedTotal,
      downloadedBytes: 0,
      totalDownloadBytes: 0,
      exportDate: apiExportDate || null,
      lastSyncDate: lastSyncDate || null
    })

    // Callback pour la progression en octets pendant le téléchargement HTTP
    const downloadProgressCallback = (downloadedBytes, totalDownloadBytes) => {
      this._emitProgress({
        isSyncing: true,
        currentEntity: entity,
        method: 'export',
        phase: 'downloading',
        downloadedBytes,
        totalDownloadBytes,
        exportDate: apiExportDate || null,
        lastSyncDate: lastSyncDate || null
      })
    }

    // Callback pour la progression en enregistrements pendant la sauvegarde IndexedDB
    const saveProgressCallback = (loaded, total) => {
      this._emitProgress({
        isSyncing: true,
        currentEntity: entity,
        method: 'export',
        phase: 'saving',
        loaded,
        total,
        downloadedBytes: 0,
        totalDownloadBytes: 0,
        exportDate: apiExportDate || null,
        lastSyncDate: lastSyncDate || null
      })
    }

    try {
      let result

      if (entity === 'properties') {
        result = await this.loadPropertiesFromExport(saveProgressCallback, downloadProgressCallback)
      } else if (entity === 'controls') {
        result = await this.loadControlsFromExport(saveProgressCallback, downloadProgressCallback)
      }

      this._emitProgress({
        isSyncing: false, currentEntity: null, method: null, phase: null,
        loaded: 0, total: 0, downloadedBytes: 0, totalDownloadBytes: 0
      })

      if (result && result.success) {
        const exportDate = result.exportGeneratedAt || apiExportDate
        // BUG FIX : lastSyncDate doit être sauvegardé à exportDate (date de génération
        // de l'export), et NON à apiLastModified.
        //
        // Problème original : après un export gzip, apiLastModified = MAX(data_refreshed_at)
        // en BDD (incluant les recalculs de scheduled_visit_at effectués APRÈS la génération
        // de l'export). Les données téléchargées ne couvrent en réalité que jusqu'à exportDate.
        // En sauvegardant lastSyncDate = apiLastModified, le prochain sync ne détectait aucun
        // delta (localSyncDate == apiLastModified) et les propriétés modifiées entre exportDate
        // et apiLastModified restaient bloquées avec leurs anciennes valeurs dans Dexie.
        //
        // Fix : lastSyncDate = exportDate → le prochain sync incrémental récupère
        // tout ce qui a changé depuis la génération de l'export.
        //
        // Garde-fou supplémentaire : si l'ancien lastSyncDate (= since passé par la sonde)
        // est déjà plus récent que exportDate (ex : nouveau ZIP régénéré mais dont le contenu
        // est antérieur à notre dernière sync), on le préserve pour éviter un retour en arrière
        // qui causerait une re-sync massive de tous les enregistrements déjà synchronisés.
        const prevSyncDate  = lastSyncDate ? this._normalizeDate(lastSyncDate) : null
        const exportDateObj = this._normalizeDate(exportDate)
        const effectiveSyncDate = (prevSyncDate && exportDateObj && prevSyncDate > exportDateObj)
          ? lastSyncDate   // Ancien lastSyncDate plus récent → le conserver
          : exportDate     // Cas normal : repartir depuis exportDate pour le rattrapage

        await this.updateLocalSyncInfo(entity, effectiveSyncDate, exportDate)

        // ── Purge totale des lots soft-deleted et hors gestion après téléchargement ZIP ──
        // La purge orpheline (lots absents du ZIP) gère les lots disparus du fichier export.
        // syncDeletions() sans `since` complète cette purge en appelant /properties/deleted
        // sans filtre de date, ce qui retourne TOUS les lots ever-deleted et hors gestion
        // expirés — indépendamment de leur date de suppression.
        //
        // Pourquoi sans `since` ici (et non avec lastSyncDate) :
        //   Si le lot a été soft-deleted AVANT la génération du ZIP (ex: supprimé le 06/10,
        //   ZIP généré le 07/10 à 2h30), le lot n'est plus dans le ZIP → purge orpheline OK.
        //   Mais si le lot était DÉJÀ en IndexedDB avant ce ZIP et que l'utilisateur n'a pas
        //   re-téléchargé le ZIP depuis la suppression (exportGenerationDate inchangée), la
        //   purge orpheline ne s'exécute pas et syncDeletions(lastSyncDate) raterait le lot
        //   si lastSyncDate > deleted_at. L'appel sans `since` garantit la purge dans tous
        //   les cas, quel que soit l'historique de sync de l'utilisateur.
        if (entity === 'properties') {
          await this.syncDeletions()
        }

        return { synced: true, method: 'export', count: result.count }
      }

      return { synced: false, reason: 'export_failed' }
    } catch (error) {
      this._emitProgress({ isSyncing: false, currentEntity: null, method: null })
      console.error(`[SyncService] Erreur téléchargement export ${entity}:`, error)
      return { synced: false, reason: 'export_error', error: error.message }
    }
  }

  /**
   * Charge les properties depuis le fichier export.
   * Phase 1 (downloading) : suivi en octets via ReadableStream → downloadProgressCallback
   * Phase 2 (saving)      : sauvegarde par lots de 1 000 enregistrements → saveProgressCallback
   *
   * @param {Function|null} saveProgressCallback     - Appelé avec (loaded, total) à chaque lot sauvegardé
   * @param {Function|null} downloadProgressCallback - Appelé avec (downloadedBytes, totalBytes) pendant le dl
   */
  async loadPropertiesFromExport(saveProgressCallback = null, downloadProgressCallback = null) {
    try {
      const { data, exportGeneratedAt } = await this.apiService.getEntityFromExport(
        'properties',
        downloadProgressCallback
      )
      
      if (data && Array.isArray(data)) {
        const BATCH_SIZE = 1000
        let saved = 0

        // ── Collecte des IDs du ZIP (avant sauvegarde) ───────────────────────
        const zipPropertyIds = new Set(data.map(p => p.id))

        for (let i = 0; i < data.length; i += BATCH_SIZE) {
          const batch = data.slice(i, i + BATCH_SIZE)
          await this.dexieService.savePropertiesBatch(batch)
          saved += batch.length
          if (saveProgressCallback) saveProgressCallback(saved, data.length)
        }

        // ── Purge des properties orphelines ──────────────────────────────────
        // Supprimer les lots présents en IndexedDB mais absents du ZIP
        // (lots supprimés, fin de gestion, etc.)
        try {
          const localIds = await this.dexieService.getAllPropertyIds()
          const orphanIds = localIds.filter(id => !zipPropertyIds.has(id))
          if (orphanIds.length > 0) {
            console.info(`[SyncService] Purge properties : ${orphanIds.length} lot(s) orphelin(s) supprimé(s) d'IndexedDB`)
            await this.dexieService.deleteProperties(orphanIds)
          }
        } catch (purgeError) {
          // Ne pas bloquer l'import si la purge échoue
          console.warn('[SyncService] Purge properties échouée (non bloquant) :', purgeError)
        }
        
        return { 
          success: true, 
          count: data.length,
          exportGeneratedAt
        }
      }
      
      return { success: false, reason: 'invalid_response' }
    } catch (error) {
      console.error('Erreur chargement properties export:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Charge les controls depuis le fichier export.
   * Phase 1 (downloading) : suivi en octets via ReadableStream → downloadProgressCallback
   * Phase 2 (saving)      : sauvegarde par lots de 1 000 enregistrements → saveProgressCallback
   *
   * @param {Function|null} saveProgressCallback     - Appelé avec (loaded, total) à chaque lot sauvegardé
   * @param {Function|null} downloadProgressCallback - Appelé avec (downloadedBytes, totalBytes) pendant le dl
   */
  async loadControlsFromExport(saveProgressCallback = null, downloadProgressCallback = null) {
    try {
      const { data, exportGeneratedAt } = await this.apiService.getEntityFromExport(
        'controls',
        downloadProgressCallback
      )
      
      if (data && Array.isArray(data)) {
        const BATCH_SIZE = 1000
        let saved = 0

        // ── Collecte des IDs du ZIP (avant sauvegarde) ───────────────────────
        const zipControlIds = new Set(data.map(c => c.id))

        for (let i = 0; i < data.length; i += BATCH_SIZE) {
          const batch = data.slice(i, i + BATCH_SIZE)
          await this.dexieService.saveControls(batch)
          saved += batch.length
          if (saveProgressCallback) saveProgressCallback(saved, data.length)
        }

        // ── Purge des controls orphelins ──────────────────────────────────────
        // Supprimer les controls présents en IndexedDB mais absents du ZIP
        // (controls dont la visite n'est plus dans le top 4 du lot, ou lot supprimé)
        try {
          const localIds = await this.dexieService.getAllControlIds()
          const orphanIds = localIds.filter(id => !zipControlIds.has(id))
          if (orphanIds.length > 0) {
            console.info(`[SyncService] Purge controls : ${orphanIds.length} control(s) orphelin(s) supprimé(s) d'IndexedDB`)
            await this.dexieService.deleteControls(orphanIds)
          }
        } catch (purgeError) {
          // Ne pas bloquer l'import si la purge échoue
          console.warn('[SyncService] Purge controls échouée (non bloquant) :', purgeError)
        }
        
        return { 
          success: true, 
          count: data.length,
          exportGeneratedAt
        }
      }
      
      return { success: false, reason: 'invalid_response' }
    } catch (error) {
      console.error('Erreur chargement controls export:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Synchronisation incrémentale (modifications depuis last sync).
   * Itère sur toutes les pages pour éviter les dépassements mémoire côté API.
   * Émet l'état de progression via _emitProgress() à chaque étape.
   *
   * Important : préserve localSync.exportGenerationDate lors de la mise à jour
   * des infos de sync, pour éviter de déclencher un re-téléchargement de l'export
   * au prochain cycle alors qu'il n'est pas nécessaire.
   */
  async syncIncremental(entity, localSync, apiLastModified) {
    // Vérifier que lastSyncDate existe, sinon arrêter le processus
    if (!localSync || !localSync.lastSyncDate) {
      return { synced: false, reason: 'no_last_sync_date' }
    }

    const since = localSync.lastSyncDate
    // 1 000 enregistrements par page (×10 vs l'ancien 100) pour réduire le nombre de requêtes
    const PAGE_LIMIT = 1000

    // ── Reprise depuis checkpoint ────────────────────────────────────────────
    // Si une sync incrémentale a été interrompue (rafraîchissement, coupure réseau…),
    // on reprend à la page suivante plutôt que de recommencer à zéro.
    const hasCheckpoint = localSync.checkpointSince === since && localSync.checkpointPage > 1
    let page         = hasCheckpoint ? localSync.checkpointPage   : 1
    let totalFetched = hasCheckpoint ? (localSync.checkpointFetched || 0) : 0

    // Notifier les listeners : début de sync pour cette entité
    this._emitProgress({
      isSyncing: true,
      currentEntity: entity,
      currentPage: page,
      totalPages: localSync.checkpointTotalPages || 0,
      totalFetched
    })

    try {
      let hasMore = true

      while (hasMore) {
        const endpoint = `/${entity}/modified?since=${encodeURIComponent(since)}&page=${page}&limit=${PAGE_LIMIT}`
        const response = await this.apiService.request(endpoint)

        if (!response || !Array.isArray(response.data)) {
          // Réponse inattendue → sauvegarder le checkpoint puis arrêter proprement
          await this._saveIncrementalCheckpoint(entity, since, page, totalFetched, response?.totalPages || 0, localSync?.exportGenerationDate)
          this._emitProgress({ isSyncing: false, currentEntity: null })
          if (page === 1) {
            return { synced: false, reason: 'invalid_response' }
          }
          break
        }

        if (response.data.length > 0) {
          // Appliquer les mises à jour de cette page dans IndexedDB
          await this.applyIncrementalUpdates(entity, response.data)
          totalFetched += response.data.length
        }

        // ── Tombstones : lots soft-deleted côté API (fusion de doublons) ──────
        // La réponse de /properties/modified contient `deletedIds` : liste des UUIDs
        // de lots marqués deleted_at != null depuis `since`. On les supprime du cache
        // IndexedDB pour qu'ils disparaissent immédiatement de la liste.
        if (entity === 'properties' && Array.isArray(response.deletedIds) && response.deletedIds.length > 0) {
          await this.dexieService.deleteProperties(response.deletedIds)
        }

        // ── Tombstones : lots "hors gestion" expirés depuis `since` ──────────
        // La réponse contient `horsGestionIds` : lots dont managementEndedAt est
        // passée depuis la dernière sync. Ces lots sont désormais exclus de l'API
        // mais restent potentiellement en IndexedDB → on les purge ici.
        if (entity === 'properties' && Array.isArray(response.horsGestionIds) && response.horsGestionIds.length > 0) {
          console.info(`[SyncService] Purge hors gestion : ${response.horsGestionIds.length} lot(s) supprimé(s) d'IndexedDB`)
          await this.dexieService.deleteProperties(response.horsGestionIds)
        }

        // Mettre à jour la progression (totalPages peut être 0 si l'API ne le retourne pas)
        const totalPages = response.totalPages || 0
        this._emitProgress({
          isSyncing: true,
          currentEntity: entity,
          currentPage: page,
          totalPages,
          totalFetched
        })

        // Continuer tant que l'API signale qu'il reste des pages
        // Compatibilité : si l'API ne renvoie pas hasMore (ancienne version), on s'arrête à la première page
        hasMore = response.hasMore === true

        // ── Checkpoint intermédiaire ─────────────────────────────────────────
        // Sauvegarder la progression après chaque page afin de pouvoir reprendre
        // là où on s'est arrêté si la page est rafraîchie ou la connexion coupée.
        if (hasMore) {
          await this._saveIncrementalCheckpoint(entity, since, page + 1, totalFetched, totalPages, localSync?.exportGenerationDate)
        }

        page++
      }

      // Fin de sync complète : effacer le checkpoint et sauvegarder la date finale
      this._emitProgress({
        isSyncing: false,
        currentEntity: null,
        currentPage: 0,
        totalPages: 0,
        totalFetched: 0
      })

      // Préserver l'exportGenerationDate existante pour ne pas forcer un re-téléchargement
      await this.updateLocalSyncInfo(entity, apiLastModified, localSync?.exportGenerationDate)
      return { synced: true, method: 'incremental', count: totalFetched }

    } catch (error) {
      // En cas d'erreur : sauvegarder le checkpoint pour reprendre plus tard
      await this._saveIncrementalCheckpoint(entity, since, page, totalFetched, 0, localSync?.exportGenerationDate)
      this._emitProgress({ isSyncing: false, currentEntity: null })

      // Si l'endpoint n'existe pas, ce n'est pas grave (toutes les entités n'en ont pas)
      if (error.response && error.response.status === 404) {
        await this.updateLocalSyncInfo(entity, apiLastModified, localSync?.exportGenerationDate)
        return { synced: true, method: 'skipped', count: 0 }
      }

      console.error(`Erreur sync incrémentale ${entity}:`, error)
      return { synced: false, reason: 'incremental_failed', error: error.message }
    }
  }

  /**
   * Sauvegarde un checkpoint intermédiaire pour reprendre une sync incrémentale
   * interrompue (rafraîchissement de page, perte de connexion…).
   *
   * Le checkpoint conserve :
   *  - `since`               : la date de départ (inchangée) pour reconstruire l'URL
   *  - `checkpointPage`      : prochaine page à récupérer
   *  - `checkpointFetched`   : nombre d'enregistrements déjà traités
   *  - `checkpointTotalPages`: totalPages retourné par la dernière réponse API (pour l'UI)
   *
   * `lastSyncDate` est laissé à `since` (non avancé) tant que la sync n'est pas
   * terminée, pour garantir qu'aucune modification n'est ignorée.
   *
   * @private
   */
  async _saveIncrementalCheckpoint(entity, since, nextPage, fetchedSoFar, totalPages, exportGenerationDate) {
    try {
      const { sync } = await import('./dexieService')
      await sync().put({
        entity,
        lastSyncDate: since,           // Prochain sync reprend depuis `since` + page checkpoint
        exportGenerationDate: exportGenerationDate || null,
        checkpointSince: since,
        checkpointPage: nextPage,
        checkpointFetched: fetchedSoFar,
        checkpointTotalPages: totalPages
      })
    } catch (e) {
      // Ne pas bloquer la sync principale si la sauvegarde du checkpoint échoue
      console.warn(`[SyncService] Checkpoint non sauvegardé pour ${entity}:`, e.message)
    }
  }

  /**
   * Applique les mises à jour incrémentales dans IndexDB
   */
  async applyIncrementalUpdates(entity, data) {
    try {
      // Utiliser les méthodes spécifiques du dexieService selon l'entité
      switch (entity) {
        case 'properties':
          await this.dexieService.savePropertiesBatch(data)
          break
        case 'controls':
          await this.dexieService.saveControls(data)
          break
        case 'teams':
          await this.dexieService.saveTeams(data)
          break
        case 'users':
          await this.dexieService.saveUsers(data)
          break
        case 'dictionaries':
          await this.dexieService.saveDictionaries(data)
          break
        case 'assistanceMandators':
          await this.dexieService.saveAssistanceMandators(data)
          break
        case 'managerMandators':
          await this.dexieService.saveManagerMandators(data)
          break
        case 'legalOwners':
          await this.dexieService.saveLegalOwners(data)
          break
        case 'interventionPoints':
          await this.dexieService.saveInterventionPoints(data)
          break
        default:
          return
      }
    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour incrémentale de ${entity}:`, error)
      throw error
    }
  }

  /**
   * Force le re-téléchargement du fichier export ZIP pour une entité.
   *
   * Utile quand :
   * - Un téléchargement ZIP précédent a été interrompu (rafraîchissement de page)
   * - Les données semblent incomplètes ou corrompues
   *
   * Fonctionnement :
   *  1. Supprime l'entrée de sync de l'entité dans IndexedDB
   *     (ce qui fait disparaître lastSyncDate et exportGenerationDate)
   *  2. Déclenche smartSync() → détecte l'absence de sync → re-télécharge le ZIP
   *
   * @param {string} entity - 'properties' | 'controls'
   * @returns {Promise<{success: boolean}>}
   */
  async forceExportDownload(entity) {
    await this.init()

    const { sync } = await import('./dexieService')

    try {
      // Effacer l'entrée de sync pour que syncWithExport détecte
      // l'absence de lastSyncDate et déclenche _downloadExport directement
      await sync().delete(entity)
    } catch (error) {
      console.error(`[SyncService] Erreur suppression sync "${entity}":`, error)
      throw error
    }

    // smartSync détectera l'absence de lastSyncDate pour cette entité
    // et déclenchera _downloadExport directement
    return await this.smartSync()
  }

  /**
   * Récupère les infos de sync locales pour une entité
   */
  async getLocalSyncInfo(entity) {
    const { sync } = await import('./dexieService')
    
    try {
      const syncInfo = await sync().get(entity)
      return syncInfo || null
    } catch (error) {
      console.error(`Erreur récupération sync info ${entity}:`, error)
      return null
    }
  }

  /**
   * Met à jour les infos de sync locales.
   * Efface également tout checkpoint intermédiaire pour signaler que la sync
   * incrémentale s'est terminée normalement (pas interrompue).
   *
   * La date est validée avant d'être sauvegardée : toute date non parseable ou
   * antérieure à l'an 2000 (ex : "0000-00-00 00:00:00") est rejetée pour éviter
   * de corrompre l'entrée de sync et de déclencher des boucles de resynchronisation.
   */
  async updateLocalSyncInfo(entity, apiLastModified, exportGenerationDate = null) {
    // Ne rien faire si pas de date du serveur
    if (!apiLastModified) {
      return
    }

    // Validation : rejeter les dates invalides ou suspicieusement anciennes.
    // Protège contre "0000-00-00 00:00:00" et autres valeurs retournées par des
    // bugs côté API qui corrompraient lastSyncDate et déclencheraient des boucles.
    const validDate = this._normalizeDate(apiLastModified)
    if (!validDate || validDate.getFullYear() < 2000) {
      console.warn(`[SyncService] updateLocalSyncInfo: date invalide ignorée pour "${entity}": "${apiLastModified}"`)
      return
    }
    
    const { sync } = await import('./dexieService')
    
    // Stocker la date du serveur dans lastSyncDate pour l'utiliser dans les prochaines requêtes.
    // checkpointSince / checkpointPage / checkpointFetched remis à null pour signaler
    // qu'aucune sync incrémentale n'est en cours (état "clean").
    const syncInfo = {
      entity,
      lastSyncDate: apiLastModified,
      exportGenerationDate,
      checkpointSince: null,
      checkpointPage: null,
      checkpointFetched: null,
      checkpointTotalPages: null
    }

    try {
      await sync().put(syncInfo)
    } catch (error) {
      console.error(`❌ Erreur mise à jour sync info ${entity}:`, error)
    }
  }

  /**
   * Déclenche une synchronisation manuelle
   */
  async manualSync() {
    return await this.smartSync()
  }

  /**
   * Synchronise les suppressions (deleted_at + hors gestion expirés).
   *
   * Appelle GET /api/properties/deleted[?since=<since>] et supprime de l'IndexedDB
   * tous les IDs retournés dans `toDeleteIds`.
   *
   * Deux modes :
   *   - Avec `since`    → purge différentielle : uniquement les lots supprimés/expirés
   *                       DEPUIS cette date. Utilisé lors de la sync incrémentale.
   *   - Sans `since` (null) → purge totale : retourne TOUS les lots soft-deleted et
   *                       hors gestion expirés, quel que soit leur âge. Utilisé après
   *                       un téléchargement ZIP pour garantir qu'aucun lot supprimé
   *                       "avant" la génération du ZIP ne reste en IndexedDB.
   *
   * @param {string|null} since - Date ISO 8601 ou null pour une purge totale.
   * @returns {Promise<{synced: boolean, deleted: number}>}
   */
  async syncDeletions(since = null) {
    await this.init()

    try {
      const endpoint = since
        ? `/properties/deleted?since=${encodeURIComponent(since)}`
        : `/properties/deleted`
      const response = await this.apiService.request(endpoint)

      if (!response || !Array.isArray(response.toDeleteIds)) {
        return { synced: false, reason: 'invalid_response' }
      }

      const toDelete = response.toDeleteIds
      if (toDelete.length > 0) {
        console.info(
          `[SyncService] syncDeletions : suppression de ${toDelete.length} lot(s) d'IndexedDB` +
          ` (deleted=${response.deletedIds?.length ?? 0}, horsGestion=${response.horsGestionIds?.length ?? 0})`
        )
        await this.dexieService.deleteProperties(toDelete)
      }

      return { synced: true, deleted: toDelete.length }
    } catch (error) {
      // Non bloquant : la purge peut échouer sans interrompre la sync principale
      console.warn('[SyncService] syncDeletions échoué (non bloquant) :', error.message)
      return { synced: false, reason: 'error', error: error.message }
    }
  }

  /**
   * Récupère le statut de sync pour l'UI
   */
  async getSyncStatus() {
    const { sync } = await import('./dexieService')
    
    try {
      const syncInfos = await sync().toArray()
      
      return syncInfos.map(info => ({
        entity: info.entity,
        lastSync: info.lastSyncDate,
        status: this.calculateSyncStatus(info.lastSyncDate)
      }))
    } catch (error) {
      console.error('Erreur récupération statut sync:', error)
      return []
    }
  }

  /**
   * Calcule le statut de fraîcheur
   */
  calculateSyncStatus(lastSyncDate) {
    if (!lastSyncDate) return 'never'
    
    try {
      const now = new Date()
      const lastSync = this._normalizeDate(lastSyncDate)
      if (!lastSync) return 'unknown'
      const hoursSinceSync = (now - lastSync) / (1000 * 60 * 60)
      
      if (hoursSinceSync < 1) return 'fresh'        // < 1h
      if (hoursSinceSync < 6) return 'recent'       // < 6h
      if (hoursSinceSync < 24) return 'ok'          // < 24h
      return 'outdated'                              // > 24h
    } catch (error) {
      return 'unknown'
    }
  }

  /**
   * Réinitialise les infos de sync (pour forcer une resynchronisation complète)
   */
  async resetSyncInfo() {
    const { sync } = await import('./dexieService')
    
    try {
      await sync().clear()
      return true
    } catch (error) {
      console.error('Erreur réinitialisation sync info:', error)
      return false
    }
  }

  // ── Scheduler de synchronisation périodique ────────────────────────────

  /**
   * Démarre la synchronisation périodique autonome.
   * Utilise des setTimeout récursifs pour éviter tout chevauchement :
   * le prochain cycle ne démarre qu'après la fin du précédent.
   *
   * À appeler une seule fois au montage de l'application (AppLayout).
   */
  startPeriodicSync() {
    this.stopPeriodicSync()
    this._scheduleNextSync()
  }

  /**
   * Arrête la synchronisation périodique.
   * À appeler au démontage de l'application (AppLayout).
   */
  stopPeriodicSync() {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer)
      this._syncTimer = null
    }
  }

  /**
   * Planifie le prochain cycle de sync après l'intervalle défini.
   * @private
   */
  _scheduleNextSync() {
    this._syncTimer = setTimeout(async () => {
      try {
        await this.smartSync()
      } catch (error) {
        console.error('[SyncService] Erreur lors de la sync périodique:', error)
      } finally {
        // Replanifier dans tous les cas (succès ou erreur)
        this._scheduleNextSync()
      }
    }, PERIODIC_SYNC_INTERVAL_MS)
  }
}

// Export d'une instance unique
export default new SyncService()
